import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { isNullOrUndefined } from 'util';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {

  constructor(private http: HttpClient) { }

  getTaskTypes(): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/task-types`;
    return this.http.get(url);
  }

  getWorkflow(entityType: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/workflow/${entityType}`;
    return this.http.get(url);
  }

  saveWorkflow(workflow: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/workflow`;
    if (workflow.id) {
      return this.http.post(url, workflow);
    } else {
      return this.http.put(url, workflow);
    }
  }

  saveGoal(goalForm: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/goal`;
    return this.http.put(url, goalForm);
  }

  getGoal(id: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/goal/${id}`;
    return this.http.get(url);
  }

  deleteEntity(id: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/${id}`;
    return this.http.delete(url);
  }

  updateField(id: string, name: string, value: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/${id}`;
    const field: any = {};
    field.name = name;
    field.value = value;
    return this.http.post(url, field);
  }

  getInitiative(id: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/initiative/${id}`;
    return this.http.get(url);
  }

  saveInitiative(value: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/initiative`;
    return this.http.put(url, value);
  }

  getProjects(): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects`;
    return this.http.get(url);
  }

  createProject(value: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects`;
    return this.http.put(url, value);
  }

  getProject(projectId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/${projectId}`;
    return this.http.get(url);
  }

  getMyProjects(): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/my-projects`;
    return this.http.get(url);
  }
  getMyProject(projectId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/${projectId}`;
    return this.http.get(url);
  }

  addTeamMember(projectId: string, person: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/team/${projectId}`;
    return this.http.post(url, person);
  }

  removeTeamMember(projectId: string, userId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/team/${projectId}/${userId}`;
    return this.http.delete(url);
  }

  deleteProject(id: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/${id}`;
    return this.http.delete(url);
  }

  saveTask(value: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/task`;
    return this.http.put(url, value);
  }

  saveMilestone(milestone: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/milestone`;
    if (!milestone.id) {
      return this.http.put(url, milestone);
    } else {
      return this.http.post(url, milestone);
    }
  }

  getMilestones(id: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/milestones/${id}`;
    return this.http.get(url);
  }

  getProjectTasks(id: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/tasks/${id}`;
    return this.http.get(url);
  }

  saveTaskMilestone(id: string, milestone: any): Observable<any> {
    const milestoneIdentity: any = {};
    if (milestone) {
      milestoneIdentity.id = milestone.id;
      milestoneIdentity.name = milestone.name;
    }
    const url = `${environment.apiOrigin}/api/projects/entities/task/${id}`;
    return this.http.post(url, milestoneIdentity);
  }

  getProjectUsers(id: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/team/${id}`;
    return this.http.get(url);
  }

  getOverdueTasks(userId): Observable<any> {
    if (isNullOrUndefined(userId)) {
      const url = `${environment.apiOrigin}/api/projects/entities/tasks/assigned/overdue/null`;
      return this.http.get(url);
    } else {
      const url = `${environment.apiOrigin}/api/projects/entities/tasks/assigned/overdue/${userId}`;
      return this.http.get(url);
    }
  }

  getAssignedTasks(userId): Observable<any> {
    if (isNullOrUndefined(userId)) {
      const url = `${environment.apiOrigin}/api/projects/entities/tasks/assigned/open/null`;
      return this.http.get(url);
    } else {
      const url = `${environment.apiOrigin}/api/projects/entities/tasks/assigned/open/${userId}`;
      return this.http.get(url);
    }
  }

  getTeamAssignedTasks(userId): Observable<any> {
    if (isNullOrUndefined(userId)) {
      const url = `${environment.apiOrigin}/api/projects/entities/tasks/team/assigned/open/null`;
      return this.http.get(url);
    } else {
      const url = `${environment.apiOrigin}/api/projects/entities/tasks/team/assigned/open/${userId}`;
      return this.http.get(url);
    }
  }

  getTask(taskId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/task/${taskId}`;
    return this.http.get(url);
  }

  getAssignedToDos(userId): Observable<any> {
    if (isNullOrUndefined(userId)) {
      const url = `${environment.apiOrigin}/api/projects/entities/to-dos/assigned/open/null`;
      return this.http.get(url);
    } else {
      const url = `${environment.apiOrigin}/api/projects/entities/to-dos/assigned/open/${userId}`;
      return this.http.get(url);
    }
  }

  getOverdueToDos(userId): Observable<any> {
    if (isNullOrUndefined(userId)) {
      const url = `${environment.apiOrigin}/api/projects/entities/to-dos/assigned/overdue/null`;
      return this.http.get(url);
    } else {
      const url = `${environment.apiOrigin}/api/projects/entities/to-dos/assigned/overdue/${userId}`;
      return this.http.get(url);
    }
  }

  getWatchList(userId): Observable<any> {
    if (isNullOrUndefined(userId)) {
      const url = `${environment.apiOrigin}/api/projects/entities/watch-list/open/null`;
      return this.http.get(url);
    } else {
      const url = `${environment.apiOrigin}/api/projects/entities/watch-list/open/${userId}`;
      return this.http.get(url);
    }
  }

  getOverdueWatchList(userId): Observable<any> {
    if (isNullOrUndefined(userId)) {
      const url = `${environment.apiOrigin}/api/projects/entities/watch-list/overdue/null`;
      return this.http.get(url);
    } else {
      const url = `${environment.apiOrigin}/api/projects/entities/watch-list/overdue/${userId}`;
      return this.http.get(url);
    }
  }

  getCompletedAssignedToDos(userId: string, pageSize: number, pageNumber: number): Observable<any> {
    const field: any = {};
    field.pageSize = pageSize;
    field.pageNumber = pageNumber;
    if (isNullOrUndefined(userId)) {
      const url = `${environment.apiOrigin}/api/projects/to-do/completed/assigned/null`;
      return this.http.post(url, field);
    } else {
      const url = `${environment.apiOrigin}/api/projects/to-do/completed/assigned/${userId}`;
      return this.http.post(url, field);
    }
  }

  saveToDo(todo: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/to-do`;
    return this.http.put(url, todo);
  }

  getToDo(toDoId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/to-do/${toDoId}`;
    return this.http.get(url);
  }

  saveEntityComment(entityId: string, value: any): Observable<any> {
    const comment: any = {};
    comment.entityId = entityId;
    comment.text = value;
    const url = `${environment.apiOrigin}/api/projects/entities/comments`;
    return this.http.put(url, comment);
  }

  getComments(toDoId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/comments/${toDoId}`;
    return this.http.get(url);
  }

  deleteComment(id: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/comments/${id}`;
    return this.http.delete(url);
  }

  updateProjectField(projectId: string, name: string, value: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/${projectId}`;
    const field: any = {};
    field.name = name;
    field.value = value;
    return this.http.post(url, field);
  }

  getMilestone(id: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/milestone/${id}`;
    return this.http.get(url);
  }

  getProjectStatus(userId: string): Observable<any> {
    if (isNullOrUndefined(userId)) {
      const url = `${environment.apiOrigin}/api/projects/status/null`;
      return this.http.get(url);
    } else {
      const url = `${environment.apiOrigin}/api/projects/status/${userId}`;
      return this.http.get(url);
    }
  }

  saveSubTask(newSubTask: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/sub-task`;
    return this.http.put(url, newSubTask);
  }

  getSubTasks(id: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/sub-tasks/${id}`;
    return this.http.get(url);
  }

  updateSubTask(subTask: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/sub-task/update`;
    return this.http.post(url, subTask);
  }

  uploadFiles(files: any, entityId: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/${entityId}/files`;
    const formData: FormData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i], files[i].name);
    }
    return this.http.put(url, formData);
  }

  getEntityFiles(entityId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/${entityId}/files`;
    return this.http.get(url);
  }

  getTimeLog(entityId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/time/${entityId}`;
    return this.http.get(url);
  }

  saveTimeLog(timeLog: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/time`;
    return this.http.put(url, timeLog);
  }

  getActivityLog(userId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/activity-log/${userId}`;
    return this.http.get(url);
  }

  updateTasks(taskUpdateDefinition: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/tasks/bulk-update`;
    return this.http.post(url, taskUpdateDefinition);
  }

  updateTask(task: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/task/update`;
    return this.http.post(url, task);
  }

  deleteFile(id: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/file/${id}`;
    return this.http.delete(url);
  }

  getFile(id: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/files/${id}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  updateToDo(toDo: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/to-do/update`;
    return this.http.post(url, toDo);
  }

  getEntityUpdateHistory(entityId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/update-history/${entityId}`;
    return this.http.get(url);
  }

  saveEntityFollowupComment(entityId: string, followupDate: any, value: any): Observable<any> {
    const comment: any = {};
    comment.entityId = entityId;
    comment.followupDate = followupDate;
    comment.text = value;
    const url = `${environment.apiOrigin}/api/projects/entities/followup-comments`;
    return this.http.put(url, comment);
  }

  archiveProject(projectId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/archive/${projectId}`;
    return this.http.post(url, {});
  }

  unArchiveProject(projectId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/un-archive/${projectId}`;
    return this.http.post(url, {});
  }

  getCategories(): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/categories`;
    return this.http.get(url);
  }

  getCategory(id: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/categories/${id}`;
    return this.http.get(url);
  }

  saveCategory(category: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/categories`;
    if (category.id) {
      return this.http.post(url, category);
    } else {
      return this.http.put(url, category);
    }
  }

  saveCustomForm(form: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/custom-form`;
    if (form._id) {
      return this.http.post(url, form);
    } else {
      return this.http.put(url, form);
    }
  }

  getCustomFormData(id: any, dataStorageCollection: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/custom-form/${id}/${dataStorageCollection}`;
    return this.http.get(url);
  }

  deleteCustomFormData(id: any, dataStorageCollection: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/custom-form/${id}/${dataStorageCollection}`;
    return this.http.delete(url);
  }

  updateProjectDetails(projectId: string, value: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/${projectId}`;
    return this.http.post(url, value);
  }

  getProjectTemplates(): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/templates`;
    return this.http.get(url);
  }

  stopWatching(removeWatchListIds: Array<string>): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/stop-watching`;
    return this.http.post(url, removeWatchListIds);
  }

  deleteTimeLog(id): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/task/log-time/${id}`;
    return this.http.delete(url);
  }

  getDetailedProjectTasks(projectId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/entities/detailed-tasks/${projectId}`;
    return this.http.get(url);
  }

  sendForApproval(projectId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/send-for-approval/${projectId}`;
    return this.http.post(url, {});
  }

  approveProject(projectId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/projects/approve-project/${projectId}`;
    return this.http.post(url, {});
  }
}
